// Данные бизнеса для страниц: интеграции, офферы, лиды (api: /api/integrations, /api/offers, /api/leads).
import { money, type Tone, time, usd, when } from '@autoaff/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface BuyoIntegration {
  connected: boolean;
  hint: string | null;
  updatedAt: string | null;
  catalog: { lastAt?: string; lastError?: string | null; offers?: number; flows?: number } | null;
  sync: {
    lastAt?: string | null;
    lastError?: string | null;
    since?: string | null;
    stats?: {
      checked: number;
      created: number;
      updated: number;
      partial: boolean;
      note: string | null;
    } | null;
  } | null;
  leads: number;
  /** Адрес постбека для кабинета buyo (с секретом) и последний пришедший; нет у старого api. */
  postback?: { url: string | null; last: { at: string; result: string } | null };
}

export interface MetaAccountView {
  id: string;
  name: string;
  businessName: string | null;
  currency: string;
  timezoneName: string | null;
  status: number;
  statusText: string;
  enabled: boolean;
  structureSyncedAt: string | null;
  insightsSyncedAt: string | null;
  historyFrom: string | null;
  lastError: string | null;
}

interface MetaJobState {
  lastAt?: string;
  lastError?: string | null;
}

export interface MetaIntegration {
  connected: boolean;
  hint: string | null;
  updatedAt: string | null;
  /** development_access | standard_access — уровень доступа приложения к Marketing API. */
  accessTier: string | null;
  accounts: MetaAccountView[];
  sync: { structure: MetaJobState | null; today: MetaJobState | null; days: MetaJobState | null };
  /** Активные объявления и сколько из них передают свой ID в ссылку. */
  ads: { active: number; tagged: number };
}

export interface TelegramIntegration {
  connected: boolean;
  botUsername: string | null;
  /** Чат алертов привязан: алерты и коды входа доходят. */
  bound: boolean;
  chats: Record<'alerts' | 'leads', { title: string | null; type: string } | null>;
  /** Ссылки привязки с одноразовым кодом: в личку и в группу. */
  links: Record<'alerts' | 'leads', { private: string; group: string; command: string } | null>;
  /** landingLeads — лиды наших лендингов сразу, с номером; leads — новые лиды из кабинета buyo списком. */
  notify: { approvals: boolean; leads: boolean; landingLeads: boolean };
  lastError: string | null;
}

export interface GeminiIntegration {
  connected: boolean;
  hint: string | null;
  updatedAt: string | null;
}

export interface Integrations {
  buyo: BuyoIntegration;
  meta: MetaIntegration;
  telegram: TelegramIntegration;
  /** Нет у старого api — тогда строка показывает «Не подключено». */
  gemini?: GeminiIntegration;
  /** fal.ai — генерации картинок и видео для креативов (этап 4). */
  fal?: GeminiIntegration;
  /** Прокси охотника за футажом (YouTube, Instagram, TikTok): подсказка — адрес без логина и пароля. */
  proxy?: GeminiIntegration & { pricePerGb: number };
}

export function useIntegrations() {
  return useQuery({
    queryKey: ['integrations'],
    queryFn: () => api<Integrations>('/api/integrations'),
    refetchInterval: 30_000,
  });
}

export interface OfferStats {
  leads: number;
  approved: number;
  /** % апрува от решённых (апрув, отказ, треш, дубль), 0…100 — одна формула платформы. */
  approveRate: number | null;
  revenueUsd: number;
}

/**
 * Экономика своего оффера (считает платформа, @autoaff/mcp economics): прогнозный апрув нового лида,
 * вероятность апрува лида в обработке по его возрасту, выплата, окупаемый и целевой CPL.
 */
export interface OfferEconomics {
  /** Доля новых лидов, которые станут апрувом (0…1). */
  approveProb: number | null;
  /**
   * Вероятность апрува лида без решения по рабочему возрасту — часам окна колл-центра 08:00–23:00 (0…1).
   * sameDay — в день заявки, пока окно не кончилось: 0, 1, 2, 4, 8, 12 ч; later — ночью и позже: 0, 1, 2, 4, 8 ч,
   * 1…5 рабочих дней (по 15 ч).
   */
  byAge: { sameDay: number[]; later: number[] } | null;
  /** % апрува от решённых у зрелых лидов (5–35 дней). */
  approveRate: number | null;
  mature: { leads: number; decided: number; approved: number };
  payoutUsd: number | null;
  /** own — средняя по своим апрувам за 30 дней, offer — ставка оффера, overall — средняя по всем офферам. */
  payoutFrom: 'own' | 'offer' | 'overall' | null;
  breakEvenCpl: number | null;
  targetCpl: number | null;
  /** Мало своих лидов — сглажено к каталогу buyo или к общей кривой. */
  thin: boolean;
  prior: 'catalog' | 'overall' | null;
}

export interface NetworkOffer {
  extId: string;
  name: string;
  image: string | null;
  inSeason: boolean;
  approvePct: number | null;
  crPct: number | null;
  holdDays: number | null;
  /** Запас товара в сети (как в кабинете buyo); null — сеть не сказала. */
  stock?: 'low' | 'medium' | 'high' | null;
  /** Можно лить на свой лендинг: false — только маркетплейс (не подходит), null — сеть не сказала. */
  landing?: boolean | null;
  /** Цена продажи в сети, сум (для лендинга). */
  priceUzs?: number | null;
  payoutUz: number | null;
  payoutCurrency: string | null;
  firstSeenAt: string;
  flows: Array<{ extId: string; name: string }>;
  /** Ваши лиды за выбранный период. */
  stats: OfferStats;
  /** Разведка (этап 6): динамика по ежедневным снимкам каталога. */
  trend?: {
    series: number[];
    approveD7: number | null;
    approveD30: number | null;
    payoutD30: number | null;
    /** Апрув × выплата: сколько в среднем приносит лид. */
    perLead: number | null;
    season: 'in' | 'out' | null;
    approveLastYear: number | null;
    ours: { id: number; slug: string; status: string } | null;
  } | null;
}

export interface ScoutSignal {
  extId: string;
  name: string;
  kind:
    | 'new'
    | 'approve_up'
    | 'approve_down'
    | 'payout_up'
    | 'payout_down'
    | 'season_in'
    | 'season_out'
    | 'stock_down';
  text: string;
  ours: boolean;
}

export interface MyOffer {
  id: number;
  slug: string;
  name: string;
  status: string;
  flowExtId: string | null;
  priceUzs: number | null;
  payoutUsd: number | null;
  /** Оффер каталога сети, к которому привязан наш. */
  networkOfferExtId?: string | null;
  /** Лендинги сети — ссылки владельца на лендинги buyo (фото и описание товара для агента). */
  networkLandings?: string[];
  stats: OfferStats;
  /** Нет у старого api. */
  economics?: OfferEconomics | null;
}

export interface OffersData {
  period?: { key: string; since: string | null; until: string };
  /** Маржа владельца (0…0.9): целевой CPL = окупаемый × (1 − маржа). Нет у старого api. */
  margin?: number;
  mine: MyOffer[];
  network: NetworkOffer[];
  signals?: ScoutSignal[];
  fetchedAt: string | null;
}

export interface LeadRow {
  id: number;
  createdAt: string;
  source: 'platform' | 'legacy' | 'network';
  name: string | null;
  phone: string | null;
  offer: string | null;
  status: string;
  rejectReason: string | null;
  payout: number | null;
  payoutCurrency: string | null;
  payoutStatus: string | null;
  holdEndAt: string | null;
  /** Риск антифрода 0–100 (лиды наших лендингов); null — не считался. */
  risk: number | null;
  /** Задержан антифродом до решения владельца. */
  held: boolean;
}

/** Деньги в валюте сети: сумы — `1 250 000 сум`, доллары — `$4.50` (DESIGN §3); общий формат платформы. */
export { money };

export type LeadFilter = 'all' | 'in_process' | 'approved' | 'rejected' | 'trash' | 'risky';
export type LeadSource = LeadRow['source'];
export type LeadSort = 'new' | 'old' | 'payout';

export interface LeadsResponse {
  period?: { key: string; since: string | null; until: string };
  items: LeadRow[];
  /**
   * Итоги и счётчики фильтров — только на первой странице. approveRate — % апрува от решённых (одна формула
   * платформы), expApproves — сколько апрувов ожидаем от лидов в обработке (по офферу и возрасту лида).
   */
  counts:
    | (Record<LeadFilter, number> & { revenue: number; approveRate?: number | null; expApproves?: number })
    | null;
  /** Офферы для фильтра: key «-» — лиды без оффера. */
  offers?: Array<{ key: string; name: string | null; n: number }> | null;
  sources?: Record<LeadSource, number> | null;
  nextCursor?: string | null;
  nextBefore: string | null;
}

/** Номер для звонка: только полный (у скрытого сетью в номере звёздочки). */
export function phoneTel(phone: string | null): string | null {
  if (!phone || phone.includes('*')) return null;
  const d = phone.replace(/\D/g, '');
  return d.length >= 9 ? `+${d}` : null;
}

/** Статус лида по-русски и цветом: зелёный и красный — только для денег и исхода (DESIGN §3). */
export const LEAD_STATUS: Record<string, { label: string; tone: Tone }> = {
  in_process: { label: 'В обработке', tone: 'info' },
  hold: { label: 'Холд', tone: 'info' },
  callback: { label: 'Перезвон', tone: 'info' },
  approved: { label: 'Апрув', tone: 'success' },
  rejected: { label: 'Отказ', tone: 'danger' },
  double: { label: 'Дубль', tone: 'warning' },
  trash: { label: 'Мусор', tone: 'default' },
};

export function leadStatus(s: string): { label: string; tone: Tone } {
  return LEAD_STATUS[s] ?? { label: s, tone: 'default' };
}

export const PAYOUT_STATUS: Record<string, string> = {
  hold: 'холд',
  available: 'доступна',
  paid: 'выплачена',
  cancelled: 'отменена',
};

export const LEAD_SOURCE: Record<LeadRow['source'], string> = {
  platform: 'Наш лендинг',
  network: 'Кабинет buyo',
  legacy: 'Panda-Manda',
};

export interface LeadEvent {
  id: number;
  at: string;
  /** created | status | payout | payout_status | delivery | capi (апрув в Meta) */
  type: string;
  oldValue: string | null;
  newValue: string | null;
  /** sync | postback | platform | import */
  source: string;
}

export interface LeadDetail {
  id: number;
  publicId: string;
  createdAt: string;
  source: LeadRow['source'];
  name: string | null;
  phone: string | null;
  offer: {
    name: string | null;
    mine: boolean;
    network: string;
    offerExtId: string | null;
    flowExtId: string | null;
    flowName: string | null;
  };
  status: string;
  rejectReason: string | null;
  payout: number | null;
  payoutCurrency: string | null;
  payoutStatus: string | null;
  holdEndAt: string | null;
  approvedAt: string | null;
  delivery: {
    status: string;
    attempts: number;
    error: string | null;
    sentAt: string | null;
    /** Сеть отказала насовсем (нет потока, неверные данные) — сама платформа не повторит. */
    permanent?: boolean;
    /** Когда платформа попробует снова; null — сама больше не отправит. */
    nextAt?: string | null;
    /** Можно нажать «Отправить заново» (лид с нашего лендинга не ушёл в сеть). */
    canResend?: boolean;
  };
  networkIds: {
    leadId: string | null;
    lid: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    syncedAt: string | null;
  };
  attribution: {
    utm: Record<'source' | 'medium' | 'campaign' | 'term' | 'content', string | null>;
    campaignId: string | null;
    adsetId: string | null;
    adId: string | null;
    placement: string | null;
  };
  events: LeadEvent[];
  raw: unknown;
  /** Лид с нашего лендинга (этап 3); у старого api и у лидов сети — нет. */
  landing?: {
    host: string;
    versionId: number | null;
    version: number | null;
    name: string | null;
    phoneEntry: string | null;
    fraudFlags: string[];
    /** Антифрод: риск 0–100, уровень и признаки с баллами. */
    fraud?: {
      score: number;
      level: FraudLevel;
      reasons: Array<{ id: string; points: number; label: string }>;
    };
    visit: { at: string; path: string; inApp: string | null; adId: string | null } | null;
  } | null;
  /** Событие Lead в Conversions API. */
  capi?: { status: string; sentAt: string | null; reason: string | null } | null;
}

const PHONE_ENTRY: Record<string, string> = {
  typed: 'телефон ввёл руками',
  autofill: 'телефон подставил браузер',
  'autofill-edit': 'телефон подставил браузер, потом поправили',
  paste: 'телефон вставили',
};
export type FraudLevel = 'low' | 'medium' | 'high';
/** Риск антифрода словами и цветом: высокий — красный (про деньги), средний — жёлтый. */
export const FRAUD_LEVEL: Record<FraudLevel, { label: string; tone: Tone }> = {
  low: { label: 'низкий риск', tone: 'default' },
  medium: { label: 'средний риск', tone: 'warning' },
  high: { label: 'высокий риск', tone: 'danger' },
};
export const capFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
export const fraudLevelOf = (score: number): FraudLevel =>
  score >= 50 ? 'high' : score >= 20 ? 'medium' : 'low';
const IN_APP: Record<string, string> = {
  instagram: 'во встроенном браузере Instagram',
  facebook: 'во встроенном браузере Facebook',
};

export function useLead(id: number | undefined) {
  return useQuery({
    queryKey: ['lead', id],
    queryFn: () => api<LeadDetail>(`/api/leads/${id}`),
    enabled: id !== undefined,
    refetchInterval: 60_000,
  });
}

const EVENT_SOURCE: Record<string, string> = {
  sync: 'синхронизация с buyo',
  postback: 'постбек buyo',
  platform: 'платформа',
  import: 'импорт',
};

export interface TimelineItem {
  key: string;
  /** null — событие ещё впереди (ждём решения, конец холда). */
  at: string | null;
  title: string;
  /** Что ещё поменялось тем же шагом: «выплата $4.50, холд». */
  detail?: string;
  sub?: string;
  tone: Tone;
}

/** Апрув в Meta: запись worker (lead_events type capi, в new_value — JSON с ответом Meta). */
export function capiStep(e: LeadEvent): TimelineItem {
  let v: {
    event?: string;
    status?: string;
    test?: boolean;
    url?: string | null;
    value?: number | null;
    reason?: string;
    error?: string | null;
  } = {};
  try {
    v = JSON.parse(e.newValue ?? '{}') as typeof v;
  } catch {
    // запись не разобрать — покажем общий заголовок
  }
  const name = v.event ?? 'Purchase';
  if (v.status === 'sent')
    return {
      key: `e${e.id}`,
      at: e.at,
      title: `Апрув ушёл в Meta — событие ${name}${v.test ? ' (проверка)' : ''}`,
      detail:
        [typeof v.value === 'number' ? usd(v.value) : null, v.url ?? null].filter(Boolean).join(' · ') ||
        undefined,
      sub: v.test ? 'виден в Events Manager → «Тестовые события»' : 'повторно этот апрув не отправится',
      tone: 'success',
    };
  if (v.status === 'error')
    return {
      key: `e${e.id}`,
      at: e.at,
      title: `Meta не приняла событие ${name}`,
      sub: v.error ?? undefined,
      tone: 'danger',
    };
  return {
    key: `e${e.id}`,
    at: e.at,
    title: 'Апрув в Meta не отправлен',
    sub: v.reason ?? undefined,
    tone: 'default',
  };
}

/** Лента лида по порядку: появление → отправка в сеть → статусы и выплата → что впереди. */
export function leadTimeline(l: LeadDetail): TimelineItem[] {
  const out: TimelineItem[] = [];
  const created = l.events.find((e) => e.type === 'created');
  const ld = l.landing;
  if (ld?.visit)
    out.push({
      key: 'visit',
      at: ld.visit.at,
      title: `Визит на ${ld.host}${ld.visit.path === '/' ? '' : ld.visit.path}`,
      sub:
        [
          ld.visit.adId ? `из объявления ${ld.visit.adId}` : 'без метки объявления',
          ld.visit.inApp ? IN_APP[ld.visit.inApp] : null,
        ]
          .filter(Boolean)
          .join(', ') || undefined,
      tone: 'default',
    });
  const bornAt = l.source === 'network' ? (l.networkIds.createdAt ?? l.createdAt) : l.createdAt;
  const seenLate =
    created && l.source === 'network' && Date.parse(created.at) - Date.parse(bornAt) > 5 * 60_000;
  out.push({
    key: 'born',
    at: bornAt,
    title:
      l.source === 'platform'
        ? 'Заявка с лендинга'
        : l.source === 'legacy'
          ? 'Лид из Panda-Manda'
          : 'Лид появился в кабинете buyo',
    sub: ld
      ? [
          ld.version ? `лендинг «${ld.name}» v${ld.version}` : ld.host,
          ld.phoneEntry ? PHONE_ENTRY[ld.phoneEntry] : null,
          ld.fraud && ld.fraud.level !== 'low'
            ? `${FRAUD_LEVEL[ld.fraud.level].label} · ${ld.fraud.score}`
            : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : l.source === 'network'
        ? `не через платформу${seenLate && created ? `, синхронизация нашла его ${sameDay(created.at, bornAt) ? `в ${time(created.at)}` : seenAt(created.at)}` : ''}`
        : undefined,
    tone: 'default',
  });

  // События одного прохода синхронизации (статус, выплата, холд) — один шаг, а не три строки
  const groups: LeadEvent[][] = [];
  for (const e of l.events) {
    if (e.type === 'created') continue;
    // Апрув в Meta (Conversions API) — своя строка: что ушло и что ответила Meta
    if (e.type === 'capi') {
      out.push(capiStep(e));
      continue;
    }
    const g = groups[groups.length - 1];
    const head = g?.[0];
    if (g && head && head.source === e.source && Math.abs(Date.parse(e.at) - Date.parse(head.at)) < 60_000)
      g.push(e);
    else groups.push([e]);
  }
  const pay = (v: string | null) => (v === null ? '—' : money(Number(v), l.payoutCurrency));
  const payoutWord = (v: string | null) => (v ? (PAYOUT_STATUS[v] ?? v) : '—');
  for (const g of groups) {
    const first = g[0] as LeadEvent;
    const via = EVENT_SOURCE[first.source] ?? first.source;
    const status = g.find((e) => e.type === 'status');
    const delivery = g.find((e) => e.type === 'delivery');
    const details: string[] = [];
    for (const e of g) {
      if (e.type === 'payout')
        details.push(
          e.oldValue === null
            ? `выплата ${pay(e.newValue)}`
            : `выплата ${pay(e.oldValue)} → ${pay(e.newValue)}`,
        );
      if (e.type === 'payout_status')
        details.push(
          e.oldValue
            ? `выплата ${payoutWord(e.oldValue)} → ${payoutWord(e.newValue)}`
            : payoutWord(e.newValue),
        );
    }
    let title: string;
    let tone: Tone = 'default';
    let sub = via;
    if (status) {
      const to = leadStatus(status.newValue ?? '');
      title = `${status.oldValue ? `${leadStatus(status.oldValue).label} → ` : 'Статус: '}${to.label}`;
      if (to.tone === 'success' || to.tone === 'danger') tone = to.tone;
    } else if (delivery) {
      const ok = delivery.newValue === 'sent';
      title = ok ? 'Отправлен в buyo' : 'Не удалось отправить в buyo';
      if (!ok) {
        tone = 'danger';
        sub = delivery.oldValue ?? via;
      }
    } else {
      const d = details.shift() ?? 'Изменение';
      title = d.charAt(0).toUpperCase() + d.slice(1);
      if (g.some((e) => e.type === 'payout_status' && e.newValue === 'cancelled')) tone = 'danger';
    }
    out.push({
      key: `e${first.id}`,
      at: first.at,
      title,
      detail: details.length ? details.join(', ') : undefined,
      sub,
      tone,
    });
  }

  // Событие Lead в Meta (Conversions API) — после отправки в сеть и статусов по времени не сортируем: одна строка
  if (l.capi)
    out.push({
      key: 'capi',
      at: l.capi.sentAt ?? l.createdAt,
      title:
        l.capi.status === 'sent'
          ? 'Событие Lead ушло в Meta'
          : l.capi.status === 'skipped'
            ? 'Событие в Meta не отправлялось'
            : l.capi.status === 'error'
              ? 'Meta не приняла событие Lead'
              : 'Событие Lead в Meta — отправляется',
      sub:
        l.capi.status === 'sent'
          ? 'тот же номер события, что у пикселя — без дубля'
          : (l.capi.reason ?? undefined),
      tone: l.capi.status === 'error' ? 'danger' : 'default',
    });

  if (l.delivery.status === 'held')
    out.push({
      key: 'held',
      at: null,
      title: 'Задержан антифродом — ждёт вашего решения',
      sub: 'не ушёл ни в сеть, ни в Meta',
      tone: 'warning',
    });
  else if (l.delivery.status === 'dropped')
    out.push({
      key: 'dropped',
      at: null,
      title: 'Не отправлен в сеть — вы решили оставить',
      tone: 'default',
    });
  else if (['in_process', 'hold', 'callback'].includes(l.status))
    out.push({
      key: 'wait',
      at: null,
      title: l.status === 'callback' ? 'Колл-центр перезвонит клиенту' : 'Ждём решения колл-центра',
      sub: 'обычно решение приходит в первые 1–2 дня',
      tone: 'info',
    });
  if (l.payoutStatus === 'hold' && l.holdEndAt && Date.parse(l.holdEndAt) > Date.now())
    out.push({
      key: 'hold',
      at: null,
      title: `Холд до ${seenAt(l.holdEndAt)}`,
      sub: 'после холда выплата станет доступной',
      tone: 'info',
    });
  return out;
}

const tashkentDay = (iso: string) => new Date(iso).toLocaleDateString('ru-RU', { timeZone: 'Asia/Tashkent' });
const sameDay = (a: string, b: string) => tashkentDay(a) === tashkentDay(b);

/** «сегодня, 14:05», «24 сент., 14:05» по Ташкенту — то же правило, что во всех списках (when). */
export function seenAt(iso: string): string {
  return when(iso);
}

// ─── реклама ───────────────────────────────────────────────────────────
/** Разрез «Рекламы»: как смотреть цифры. */
export type AdsView = 'campaigns' | 'ads' | 'offers' | 'creatives' | 'days';
/** running — включены сейчас (и на проверке), stopped — не включены, но были в периоде. */
export type AdsState = 'all' | 'running' | 'stopped';

/** Что спросить у api: период — ключ @autoaff/schemas/period; пустая строка — фильтр не задан. */
export interface AdsParams {
  period: string;
  view: AdsView;
  account: string;
  offer: string;
  state: AdsState;
  q: string;
}

/** Цифры строки: считает платформа (packages/mcp/src/metrics.ts). Деньги — в USD по курсу ЦБ дня. */
export interface AdMetrics {
  spend: number;
  impressions: number;
  linkClicks: number;
  leads: number;
  approved: number;
  inProcess: number;
  rejected: number;
  revenue: number;
  ctr: number | null;
  cpc: number | null;
  cpm: number | null;
  cpl: number | null;
  /** % апрува от решённых (апрув, отказ, треш, дубль). */
  approveRate: number | null;
  approveCost: number | null;
  profit: number;
  /** Ждём ещё выплат от лидов в обработке: вероятность апрува по офферу и возрасту лида × выплата. */
  expected: number;
  forecastProfit: number;
  /** Ждём апрувов от лидов в обработке (сумма вероятностей). */
  expApproves?: number;
  /** Апрувы с прогнозом и прогнозная цена апрува. */
  approvesForecast?: number;
  approveCostForecast?: number | null;
  /** Частота (показы / охват по дням) и досмотр 3 с от показов, %. */
  frequency?: number | null;
  hookRate?: number | null;
  /** Окупаемый и целевой CPL оффера строки; null — оффер у строки неизвестен или их несколько. */
  breakEvenCpl?: number | null;
  targetCpl?: number | null;
  /** Мало данных по офферу — CPL сглажены к каталогу или общим цифрам. */
  econThin?: boolean;
}

export interface AdIssue {
  level: string | null;
  code: number | null;
  summary: string;
  type: string | null;
}

interface AdObject extends AdMetrics {
  id: string;
  name: string;
  status: string | null;
  effectiveStatus: string | null;
  issues: AdIssue[] | null;
}

export interface AdView extends AdObject {
  thumbnailUrl: string | null;
  previewUrl: string | null;
  videoId: string | null;
  tagsAdId: boolean;
  /** Ролик из пула, если объявление с ним связано. */
  creative: { id: number; code: string; slug: string; offer: string } | null;
  review: Record<string, string> | null;
}

/** Объявление в разрезе «Объявления»: с кампанией и адсетом. */
export interface AdListRow extends AdView {
  campaignId: string | null;
  campaignName: string | null;
  adsetId: string | null;
  adsetName: string | null;
}

export interface Budget {
  kind: 'day' | 'lifetime';
  amount: number | null;
}

export interface AdsetView extends AdObject {
  learning: string | null;
  budget: Budget | null;
  ads: AdView[];
}

export interface CampaignView extends AdObject {
  accountId: string;
  objective: string | null;
  budget: Budget | null;
  adsets: AdsetView[];
}

/** Оффер в разрезе «Офферы»: id — число строкой или 'none' (без оффера). */
export interface OfferRow extends AdMetrics {
  id: string;
  name: string;
  slug: string | null;
  /** Сколько объявлений оффера показано за период. */
  ads: number;
}

/** Ролик пула в разрезе «Креативы»: id — id ролика строкой или 'none' (объявления без ролика). */
export interface CreativeRow extends AdMetrics {
  id: string;
  name: string;
  code: string | null;
  title: string | null;
  slug: string | null;
  offer: string | null;
  thumbnailUrl: string | null;
  ads: number;
  /** Ролик динамического объявления: лиды, апрувы и выплаты поделены по доле в лидах Meta — оценка. */
  estimated?: boolean;
}

export interface DayRow extends AdMetrics {
  day: string;
}

export interface ForeignCampaign {
  id: string;
  name: string;
  effectiveStatus: string | null;
  budget: Budget | null;
  spend: number;
}

export type ChangeKey =
  | 'spend'
  | 'impressions'
  | 'linkClicks'
  | 'ctr'
  | 'cpc'
  | 'cpm'
  | 'leads'
  | 'cpl'
  | 'approved'
  | 'approveRate'
  | 'approveCost'
  | 'revenue';

/**
 * Сравнение с прошлым периодом той же длины (у «сегодня» — со вчера к этому же часу, у «этого месяца» — с теми же
 * днями прошлого). Апрувы прошлого периода — «на тот момент» (asOf): решённые к тому же сроку, что и сейчас.
 */
export interface AdsCompare {
  kind: 'range' | 'same_time';
  since: string;
  until: string;
  at: string | null;
  asOf?: string | null;
  /** Расход прошлого периода известен (есть статистика) — иначе расходные сравнения пусты. */
  spendKnown: boolean;
  totals: AdMetrics;
  /** Изменение, % от прошлого; % апрува — в п.п.; null — сравнивать не с чем. */
  change: Record<ChangeKey, number | null>;
  /** Изменение прибыли в деньгах. */
  diff: { profit: number | null; forecastProfit: number | null };
}

export interface AdsData {
  /** since — первый день ряда (у «всего времени» — первый день с данными). */
  period: { key: string; since: string | null; until: string };
  compare: AdsCompare | null;
  filters: { account: string | null; offer: string | null; state: AdsState; q: string };
  /** Выбор в фильтрах: count — объявлений при остальных фильтрах. */
  options: {
    accounts: Array<{ id: string; name: string; count: number }>;
    offers: Array<{ id: string; name: string; count: number }>;
    states: Record<AdsState, number>;
  };
  currency: string;
  accounts: Array<{
    id: string;
    name: string;
    enabled: boolean;
    status: number;
    statusText: string;
    currency: string;
    insightsSyncedAt: string | null;
    historyFrom: string | null;
  }>;
  totals: AdMetrics;
  attributedLeads: number;
  /** По дням периода — для графика и разреза «Дни». */
  days: DayRow[];
  view: AdsView;
  campaigns: CampaignView[];
  ads: AdListRow[];
  offers: OfferRow[];
  creatives: CreativeRow[];
  /** Кампании друга владельца: в итоги и строки не входят. */
  foreign: ForeignCampaign[];
  health: { activeAds: number; activeTagged: number; activeLinked: number; disapproved: number };
  rates: { approveRate: number | null; avgPayout: number | null };
  /** Включённые кабинеты не в поясе Ташкента: их дни не совпадают с днями лидов. Нет у старого api. */
  tzMismatch?: Array<{ id: string; name: string; timezone: string | null }>;
  /** Маржа владельца (0…0.9). Нет у старого api. */
  margin?: number;
  syncedAt: string | null;
}

/** Отметить кампанию «не моя» (кампания друга — не учитывается) или вернуть её в учёт. */
export function markForeign(id: string, foreign: boolean) {
  return api(`/api/ads/campaigns/${id}/foreign`, { foreign }, 'PUT');
}

/** Цифры «Рекламы». Пока грузится новый выбор, видны прошлые цифры (без мигания скелетонов). */
export function useAds(p: AdsParams) {
  const qs = new URLSearchParams(Object.entries(p).filter(([, v]) => v !== '' && v !== 'all')).toString();
  return useQuery({
    queryKey: ['ads', p],
    queryFn: () => api<AdsData>(`/api/ads?${qs}`),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

/** Статус объекта рекламы по-русски; красный — только когда реклама не крутится из-за проблемы. */
const AD_STATUS: Record<string, { label: string; tone: Tone }> = {
  ACTIVE: { label: 'Активно', tone: 'success' },
  PAUSED: { label: 'Пауза', tone: 'default' },
  CAMPAIGN_PAUSED: { label: 'Кампания на паузе', tone: 'default' },
  ADSET_PAUSED: { label: 'Адсет на паузе', tone: 'default' },
  PENDING_REVIEW: { label: 'На проверке', tone: 'warning' },
  IN_PROCESS: { label: 'Обрабатывается', tone: 'info' },
  PREAPPROVED: { label: 'Одобрено', tone: 'info' },
  WITH_ISSUES: { label: 'Есть проблемы', tone: 'warning' },
  DISAPPROVED: { label: 'Отклонено', tone: 'danger' },
  PENDING_BILLING_INFO: { label: 'Нужна оплата', tone: 'danger' },
  ARCHIVED: { label: 'В архиве', tone: 'default' },
  DELETED: { label: 'Удалено', tone: 'default' },
};

export function adStatus(s: string | null): { label: string; tone: Tone } {
  return (s && AD_STATUS[s]) || { label: s ?? '—', tone: 'default' };
}

// ─── сводка ────────────────────────────────────────────────────────────
export interface DayMetrics extends AdMetrics {
  day: string;
}

export interface SummaryData {
  today: DayMetrics;
  yesterday: DayMetrics;
  /** Вчера к этому же часу: расход — из снимков внутри дня (null — снимков ещё нет), лиды — по времени создания. */
  sameTimeYesterday: { spend: number | null; leads: number };
  days: DayMetrics[];
  /** Расход кампаний друга владельца (в цифры выше не входит): сегодня и за те же 14 дней. */
  foreign: { today: number; days: number };
  rates: { approveRate: number | null; avgPayout: number | null };
  connected: { buyo: boolean; meta: boolean; accounts: number };
  spendSyncedAt: string | null;
}

export function useSummary() {
  return useQuery({
    queryKey: ['summary'],
    queryFn: () => api<SummaryData>('/api/summary'),
    refetchInterval: 60_000,
  });
}
